import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { convitesService } from '@/services/convitesService'
import { tenantService } from '@/services/tenantService'
import { ConviteEmitido } from '@/types/candidatura'
import { TenantAdmin } from '@/types/tenant'
import { CANAIS_COMERCIAIS, CANAL_LABEL } from '@/types/statusReserva'

interface Props {
  aberto: boolean
  /** ADMIN escolhe o tenant; GERENTE convida no próprio (o servidor lê o claim do JWT). */
  escolheTenant: boolean
  onFechar: () => void
  /** Chamado depois de um convite emitido — a página recarrega os pendentes. */
  onEmitido: () => void
}

const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * "+ Convidar usuário" (Onda 7.3, gate C4): atalho do back-office que cria a candidatura
 * já aprovada e emite o convite no mesmo passo. A candidatura pública ("Quero ser
 * consultor") continua existindo ao lado — este diálogo não a substitui.
 *
 * O perfil é fixo em Consultor de propósito: OPERADOR e GERENTE nascem por "Novo
 * usuário", que não passa por convite. O link aparece UMA vez, como no funil.
 */
export const ConvidarUsuarioDialog: FC<Props> = ({ aberto, escolheTenant, onFechar, onEmitido }) => {
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [canal, setCanal] = useState('')
  const [tenants, setTenants] = useState<TenantAdmin[]>([])
  const [erroTenants, setErroTenants] = useState<string | null>(null)

  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [emitido, setEmitido] = useState<ConviteEmitido | null>(null)

  useEffect(() => {
    if (!aberto) return
    setNome('')
    setEmail('')
    setTenantId('')
    setCanal('')
    setErro(null)
    setEmitido(null)
    if (!escolheTenant) return
    setErroTenants(null)
    tenantService
      .listar()
      .then((lista) => setTenants(lista.filter((t) => t.ativo)))
      .catch((e) => setErroTenants(e instanceof Error ? e.message : 'Falha ao listar tenants'))
  }, [aberto, escolheTenant])

  const podeEnviar =
    !!nome.trim() && EMAIL_OK.test(email.trim()) && !!canal && (!escolheTenant || !!tenantId) && !enviando

  const enviar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      const r = await convitesService.convidar({
        nome: nome.trim(),
        email: email.trim(),
        perfil: 'CONSULTOR',
        tenantId: escolheTenant ? tenantId : undefined,
        canal,
      })
      setEmitido(r)
      onEmitido()
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Não foi possível emitir o convite')
    } finally {
      setEnviando(false)
    }
  }

  const linkAbsoluto = (c: ConviteEmitido) => `${window.location.origin}${c.link}`

  return (
    <Dialog open={aberto} onClose={enviando ? undefined : onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Convidar usuário</DialogTitle>
      <DialogContent>
        {emitido ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {emitido.emailEnviado ? (
              <Alert severity="success">
                Convite enviado para <strong>{emitido.emailDestinatario}</strong>. O link
                abaixo é uma cópia — vale até{' '}
                {new Date(emitido.expiraEm).toLocaleString('pt-BR')}.
              </Alert>
            ) : (
              <Alert severity="warning">
                O e-mail não saiu. Copie o link agora e envie você mesmo — ele não pode
                ser recuperado depois; só o hash fica guardado.
              </Alert>
            )}
            <TextField
              label="Link do convite"
              value={linkAbsoluto(emitido)}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Cria a candidatura já aprovada e emite o convite no mesmo passo. A
              candidatura pública continua disponível para quem chega por conta própria.
            </Typography>
            {erro && <Alert severity="error">{erro}</Alert>}
            {erroTenants && <Alert severity="error">{erroTenants}</Alert>}
            <TextField
              label="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="E-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Perfil"
              value="Consultor"
              fullWidth
              InputProps={{ readOnly: true }}
              helperText="Só consultores entram por convite; operadores e gerentes nascem em “Novo usuário”."
            />
            {escolheTenant ? (
              <TextField
                select
                label="Tenant"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                required
                fullWidth
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.nome}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Tenant"
                value="O seu tenant"
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Gerente convida só para o próprio tenant."
              />
            )}
            <TextField
              select
              label="Canal comercial"
              value={canal}
              onChange={(e) => setCanal(e.target.value)}
              required
              fullWidth
            >
              {CANAIS_COMERCIAIS.map((c) => (
                <MenuItem key={c} value={c}>
                  {CANAL_LABEL[c]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {emitido ? (
          <>
            <Button onClick={() => navigator.clipboard?.writeText(linkAbsoluto(emitido))}>
              Copiar link
            </Button>
            <Button variant="contained" onClick={onFechar}>
              Fechar
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onFechar} disabled={enviando}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={enviar}
              disabled={!podeEnviar}
              startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {enviando ? 'Emitindo…' : 'Emitir convite'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ConvidarUsuarioDialog
